import React, { useCallback, useState } from 'react';
import { Alert, Button, Card, Form, Modal, Table } from 'react-bootstrap';
import { adminError } from './api';
import { PageTitle, ResourceState, Seats, useAdminApi, useAdminResource } from './shared';

export default function Employees() {
  const api = useAdminApi();
  const [status, setStatus] = useState('ACTIVE');
  const resource = useAdminResource(useCallback(() => api.employees(status), [api, status]));
  const [email, setEmail] = useState(''); const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false); const [error, setError] = useState('');
  const [notice, setNotice] = useState(''); const [confirm, setConfirm] = useState(null);
  const [setup, setSetup] = useState(null); const [copyNotice, setCopyNotice] = useState('');

  const create = async event => {
    event.preventDefault(); setBusy(true); setError(''); setNotice('');
    try {
      const created = await api.createEmployee({ email: email.trim(), displayName: displayName.trim() });
      setEmail(''); setDisplayName(''); setCopyNotice('');
      setSetup(created.passwordSetupToken ? {
        email: created.email,
        link: `${window.location.origin}/password/setup#setup=${encodeURIComponent(created.passwordSetupToken)}`,
      } : null);
      setNotice('Employee created. The employee role is USER.');
      await resource.reload();
    } catch (failure) { setError(adminError(failure)); }
    finally { setBusy(false); }
  };
  const changeStatus = async (employee, next) => {
    setBusy(true); setError(''); setNotice('');
    try {
      await api.setEmployeeStatus(employee.id, next);
      setConfirm(null);
      setNotice(next === 'ACTIVE' ? 'Employee reactivated.' : 'Employee deactivated.');
      await resource.reload();
    } catch (failure) { setConfirm(null); setError(adminError(failure)); }
    finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(setup.link); setCopyNotice('Setup link copied. Share it privately with this employee.'); }
    catch { setCopyNotice('Clipboard access is unavailable. Select and copy the link manually.'); }
  };

  return <section className="ta-page">
    <PageTitle title="Employees" />
    <p>Employees are provisioned directly with the USER role. Seat availability and employee changes are validated by the server.</p>
    {error && <Alert variant="danger" role="alert">{error}</Alert>}
    {notice && <Alert variant="success" role="status">{notice}</Alert>}
    {setup && <Card className="ta-setup"><Card.Body>
      <h2>Password setup for {setup.email}</h2>
      <p>Copy this one-time link before leaving this page. It expires after 24 hours. Share it privately; no email or OTP is sent.</p>
      <Form.Label htmlFor="employee-setup-link">Password setup link</Form.Label>
      <Form.Control id="employee-setup-link" value={setup.link} readOnly onFocus={e => e.target.select()} />
      <div className="ta-actions"><Button onClick={copy}>Copy setup link</Button><Button variant="outline-secondary" onClick={() => { setSetup(null); setCopyNotice(''); }}>Dismiss setup link</Button></div>
      {copyNotice && <p role="status">{copyNotice}</p>}
    </Card.Body></Card>}

    <Card><Card.Body><h2>Add employee</h2><Form onSubmit={create} aria-label="Add employee">
      <div className="ta-form-grid">
        <Form.Group controlId="employee-email"><Form.Label>Email</Form.Label><Form.Control type="email" maxLength={320} autoComplete="off" value={email} required disabled={busy} onChange={e => setEmail(e.target.value)} /></Form.Group>
        <Form.Group controlId="employee-name"><Form.Label>Display name</Form.Label><Form.Control maxLength={200} autoComplete="off" value={displayName} required disabled={busy} onChange={e => setDisplayName(e.target.value)} /></Form.Group>
      </div>
      <p className="ta-hint">Use the organization’s primary email domain. Reactivate an existing deactivated employee instead of adding a duplicate.</p>
      <Button type="submit" disabled={busy}>{busy ? 'Please wait…' : 'Add employee'}</Button>
    </Form></Card.Body></Card>

    <div className="ta-actions" role="group" aria-label="Employee status filter">
      {['ACTIVE', 'DEACTIVATED'].map(value => <Button key={value} variant={status === value ? 'primary' : 'outline-secondary'} aria-pressed={status === value} disabled={busy} onClick={() => setStatus(value)}>{value === 'ACTIVE' ? 'Active' : 'Deactivated'}</Button>)}
      <Button variant="outline-secondary" disabled={busy || resource.loading} onClick={resource.reload}>Refresh employees</Button>
    </div>
    <ResourceState resource={resource}>
      <Seats seats={resource.data?.seats} />
      <p>Total employees: {resource.data?.total ?? 'Not provided'} · Active: {resource.data?.active ?? 'Not provided'} · Deactivated: {resource.data?.deactivated ?? 'Not provided'}</p>
      {resource.data?.users?.length ? <Table responsive className="ta-table"><caption>{status === 'ACTIVE' ? 'Active' : 'Deactivated'} employees</caption>
        <thead><tr><th scope="col">Email</th><th scope="col">Display name</th><th scope="col">Status</th><th scope="col">Actions</th></tr></thead>
        <tbody>{resource.data.users.map(employee => <tr key={employee.id}>
          <td>{employee.email}</td><td>{employee.displayName}</td><td>{employee.status}</td><td>
            {employee.status === 'ACTIVE' ? <Button variant="outline-danger" disabled={busy} aria-label={`Deactivate ${employee.email}`} onClick={() => setConfirm(employee)}>Deactivate</Button> :
              <Button variant="outline-secondary" disabled={busy} aria-label={`Reactivate ${employee.email}`} onClick={() => changeStatus(employee, 'ACTIVE')}>Reactivate</Button>}
          </td>
        </tr>)}</tbody>
      </Table> : <p>No {status === 'ACTIVE' ? 'active' : 'deactivated'} employees to display.</p>}
    </ResourceState>

    <Modal show={!!confirm} onHide={() => !busy && setConfirm(null)} centered className="tenant-admin-modal" backdropClassName="ta-modal-backdrop" aria-labelledby="deactivate-title">
      <Modal.Header><Modal.Title as="h2" id="deactivate-title">Deactivate employee?</Modal.Title></Modal.Header>
      <Modal.Body><p>Deactivate {confirm?.email}? Their access will be removed and their active seat released. Reactivation later requires an available seat.</p></Modal.Body>
      <Modal.Footer><Button variant="outline-secondary" disabled={busy} onClick={() => setConfirm(null)}>Cancel</Button><Button variant="danger" disabled={busy} onClick={() => changeStatus(confirm, 'DEACTIVATED')}>Confirm deactivation</Button></Modal.Footer>
    </Modal>
  </section>;
}
